import { useState } from 'react';
import {
  Box, Button, TextField, Snackbar, IconButton, InputAdornment, Stack,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircle from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import LogoImage from '../common/auth/LogoImage';
import AuthCard from '../common/auth/AuthCard';

const useStyles = makeStyles()((theme) => ({
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "8px",
    background: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    "&:focus-within": {
      background: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
      : "rgba(0, 0, 0, 0.08)",
    },
  },
  primaryButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "white",
    fontWeight: "bold",
    padding: "14px 0",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    fontSize: "1rem",
    minHeight: "44px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
    },
    transition: "all 0.2s ease",
  },
  secondaryButton: {
    padding: "10px 0",
    fontWeight: 600,
    minHeight: "40px",
  },
}));

const RegisterPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const handleRegister = async (event) => {
    event.preventDefault();
    if (password !== confirm) {
      setSnackbar(t('registerPasswordMismatch'));
      return;
    }
    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, password }),
      });
      if (response.ok) {
        navigate('/');
      } else {
        setSnackbar(await response.text());
      }
    } catch (error) {
      setSnackbar(error.message);
    }
  };

  return (
    <LoginLayout>
      <Box
        component="form"
        onSubmit={handleRegister}
        sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        <AuthCard>
          <LogoImage />

          <TextField
            required
            fullWidth
            placeholder={t("userName")}
            name="name"
            value={name}
            autoComplete="name"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              ),
              className: classes.input,
              disableUnderline: true,
            }}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            required
            fullWidth
            placeholder={t("userEmail")}
            name="email"
            value={email}
            autoComplete="email"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
              className: classes.input,
              disableUnderline: true,
            }}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            required
            fullWidth
            placeholder={t("userPassword")}
            name="password"
            value={password}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
              className: classes.input,
              disableUnderline: true,
            }}
            onChange={(e) => setPassword(e.target.value)}
          />

          <TextField
            required
            fullWidth
            placeholder={t("userPasswordConfirmation")}
            name="confirm"
            value={confirm}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              className: classes.input,
              disableUnderline: true,
            }}
            onChange={(e) => setConfirm(e.target.value)}
          />

          <Stack spacing={1.2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!email || !password || !confirm || !name}
              className={classes.primaryButton}
              fullWidth
            >
              {t('loginRegister')}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              color="primary"
              className={classes.secondaryButton}
              fullWidth
            >
              {t('loginLogin')}
            </Button>
          </Stack>
        </AuthCard>
      </Box>

      {/* Snackbar mensagens */}
      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setSnackbar('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default RegisterPage;
